import type { FieldConfig } from 'src/types/form-config.types';

import { type ReactNode } from 'react';
import { Controller, type Control } from 'react-hook-form';

import Grid from '@mui/material/Grid2';
import { Box, Chip, Stack, MenuItem, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';

import { Field } from 'src/components/hook-form';
import { ColorPicker } from 'src/components/color-utils';
import { useSettingsContext } from 'src/components/settings';

/**
 * Universal Field Renderer
 *
 * Renders form fields based on configuration using switch-case pattern
 * Supports all common field types and custom components
 */

interface FieldRendererProps {
  field: FieldConfig;
  control: Control<any>;
  formValues?: any;
}

export function FieldRenderer({ field, control, formValues }: FieldRendererProps) {
  const { t } = useTranslate('translation');
  const settings = useSettingsContext();

  // Check conditional rendering
  if (field.condition && !field.condition(formValues)) {
    return null;
  }

  // Helper to get translated label
  const getLabel = (label: string) => {
    const translatedLabel = t(label);
    if (field.required) {
      return `${translatedLabel} *`;
    }
    return translatedLabel;
  };

  // Render field based on type using switch-case
  const renderField = (): ReactNode => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <Field.Text
            name={field.name}
            label={getLabel(field.label)}
            type={field.type}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            disabled={field.disabled}
            helperText={field.helperText ? t(field.helperText) : undefined}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        );

      case 'phone':
        return (
          <Field.Phone
            name={field.name}
            label={getLabel(field.label)}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            disabled={field.disabled}
            helperText={field.helperText ? t(field.helperText) : undefined}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        );

      case 'textarea':
        return (
          <Field.Text
            name={field.name}
            label={getLabel(field.label)}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            disabled={field.disabled}
            helperText={field.helperText ? t(field.helperText) : undefined}
            multiline
            rows={4}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        );

      case 'select':
        return (
          <Field.Select
            name={field.name}
            label={getLabel(field.label)}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            disabled={field.disabled}
            helperText={field.helperText ? t(field.helperText) : undefined}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          >
            {field.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </Field.Select>
        );

      case 'autocomplete':
        return (
          <Field.Autocomplete
            name={field.name}
            label={getLabel(field.label)}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            disabled={field.disabled}
            helperText={field.helperText ? t(field.helperText) : undefined}
            options={field.options || []}
            getOptionLabel={(option) => option.label || option}
            slotProps={{ textfield: { slotProps: { inputLabel: { shrink: true } } } }}
            fullWidth
            {...field.componentProps}
          />
        );

      case 'multiSelect':
      case 'multiAutocomplete':
        return (
          <Field.Autocomplete
            name={field.name}
            label={getLabel(field.label)}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            disabled={field.disabled}
            helperText={field.helperText ? t(field.helperText) : undefined}
            options={field.options || []}
            getOptionLabel={(option) => option.label || option}
            multiple
            disableCloseOnSelect
            slotProps={{ textfield: { slotProps: { inputLabel: { shrink: true } } } }}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value || index}
                  label={option.label || option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            fullWidth
            {...field.componentProps}
          />
        );

      case 'multiAssign':
        // Special case for assignUsers field with custom component
        if (field.component) {
          const AssignComponent = field.component;
          return (
            <Box sx={{ width: '100%' }}>
              <AssignComponent
                {...field.componentProps}
                name={field.name}
                label={getLabel(field.label)}
                fullWidth
                placeholder={
                  field.componentProps?.placeholder
                    ? t(field.componentProps.placeholder)
                    : undefined
                }
              />
            </Box>
          );
        }
        return null;

      case 'checkbox':
        return (
          <Field.Checkbox
            name={field.name}
            label={getLabel(field.label)}
            helperText={field.helperText ? t(field.helperText) : undefined}
            disabled={field.disabled}
          />
        );

      case 'switch':
        return (
          <Field.Switch
            name={field.name}
            label={getLabel(field.label)}
            helperText={field.helperText ? t(field.helperText) : undefined}
            disabled={field.disabled}
          />
        );

      case 'date':
        return (
          <Field.DatePicker
            name={field.name}
            label={getLabel(field.label)}
            disabled={field.disabled}
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: field.helperText ? t(field.helperText) : undefined,
                slotProps: { inputLabel: { shrink: true } },
              },
            }}
            {...field.componentProps}
          />
        );

      case 'time':
        return (
          <Field.TimePicker
            name={field.name}
            label={getLabel(field.label)}
            disabled={field.disabled}
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: field.helperText ? t(field.helperText) : undefined,
                slotProps: { inputLabel: { shrink: true } },
              },
            }}
            {...field.componentProps}
          />
        );

      case 'datetime':
        return (
          <Field.MobileDateTimePicker
            name={field.name}
            label={getLabel(field.label)}
            disabled={field.disabled}
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: field.helperText ? t(field.helperText) : undefined,
                slotProps: { inputLabel: { shrink: true } },
              },
            }}
            {...field.componentProps}
          />
        );

      case 'editor':
        return (
          <Stack spacing={1}>
            <Typography variant="subtitle2">{getLabel(field.label)}</Typography>
            <Field.Editor
              name={field.name}
              sx={{ maxHeight: 480 }}
              direction={settings.state.direction === 'ltr' ? 'left' : 'right'}
            />
            {field.helperText && (
              <Typography variant="caption" color="text.secondary">
                {t(field.helperText)}
              </Typography>
            )}
          </Stack>
        );

      case 'customField':
        // Handle dynamic custom fields (from customFields array)
        if (field.componentProps?.fieldData) {
          const customFieldData = field.componentProps.fieldData;
          const {
            name,
            label,
            type: fieldType,
            options,
            isVisible,
            isDisabled,
            isRequired,
          } = customFieldData;

          // Check visibility
          if (isVisible !== true && isVisible !== 'Yes') return null;

          // Add required indicator to label (handle both boolean and string)
          const isFieldRequired = isRequired === true || isRequired === 'Yes';
          const fieldLabel = isFieldRequired ? `${label || ''} *` : label || '';

          // Render based on custom field type
          switch (fieldType) {
            case 'text':
              return (
                <Field.Text
                  name={name || ''}
                  label={fieldLabel}
                  disabled={isDisabled}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
              );

            case 'number':
              return (
                <Field.Number
                  name={name || ''}
                  label={fieldLabel}
                  disabled={isDisabled}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
              );

            case 'boolean':
              return <Field.Checkbox name={name || ''} label={fieldLabel} disabled={isDisabled} />;

            case 'select':
              return (
                <Field.Select
                  name={name || ''}
                  label={fieldLabel}
                  disabled={isDisabled}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                >
                  {options && options.length > 0 ? (
                    options.map((option: string) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No Option</MenuItem>
                  )}
                </Field.Select>
              );

            case 'multiSelect':
              return (
                <Field.MultiSelect
                  name={name || ''}
                  label={fieldLabel}
                  disabled={isDisabled}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                  chip
                  options={
                    options
                      ? options.map((option: string) => ({ label: option, value: option }))
                      : []
                  }
                />
              );

            default:
              return (
                <Field.Text
                  name={name || ''}
                  label={fieldLabel}
                  disabled={isDisabled}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
              );
          }
        }
        return null;

      case 'colorPicker':
        return (
          <Stack spacing={1}>
            <Typography variant="subtitle2">{getLabel(field.label)}</Typography>
            <Controller
              name={field.name}
              control={control}
              render={({ field: controllerField }) => (
                <ColorPicker
                  options={field.options?.map((o) => o.value) || []}
                  value={controllerField.value}
                  onChange={(color) => controllerField.onChange(color)}
                />
              )}
            />
          </Stack>
        );

      case 'color':
        return (
          <Field.Text
            name={field.name}
            label={getLabel(field.label)}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            type="color"
            slotProps={{ inputLabel: { shrink: true } }}
            {...field.componentProps}
          />
        );

      case 'custom':
        if (field.component) {
          const CustomComponent = field.component;
          return (
            <CustomComponent
              name={field.name}
              control={control}
              label={getLabel(field.label)}
              {...field.componentProps}
            />
          );
        }
        return null;

      default:
        console.warn(`Unknown field type: ${field.type}`);
        return null;
    }
  };

  // Wrap in Grid if gridSize is specified
  if (field.gridSize) {
    return (
      <Grid size={field.gridSize}>
        <Box sx={{ my: 1 }}>{renderField()}</Box>
      </Grid>
    );
  }

  return <Box sx={{ my: 1 }}>{renderField()}</Box>;
}

/**
 * Render Multiple Fields
 *
 * Helper to render an array of fields
 */
interface FieldsRendererProps {
  fields: FieldConfig[];
  control: Control<any>;
  formValues?: any;
  useGrid?: boolean;
  spacing?: number | string;
}

export function FieldsRenderer({
  fields,
  control,
  formValues,
  useGrid = false,
  spacing = 1,
}: FieldsRendererProps) {
  if (useGrid) {
    return (
      <Grid container spacing={spacing}>
        {fields.map((field) => (
          <FieldRenderer key={field.name} field={field} control={control} formValues={formValues} />
        ))}
      </Grid>
    );
  }

  return (
    <>
      {fields.map((field) => (
        <FieldRenderer key={field.name} field={field} control={control} formValues={formValues} />
      ))}
    </>
  );
}
  